import { Typography } from 'antd'
import {
  Boxes,
  Building2,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
  Network,
  User,
  Users
} from 'lucide-react'
import type React from 'react'
import { useTranslation } from 'react-i18next'

import type { OrgChartNode as OrgChartNodeType, OrgNodeType } from './types'

const { Text } = Typography

type Props = {
  node: OrgChartNodeType
  isSelected?: boolean
  isCollapsed?: boolean
  hasChildren?: boolean
  onClick?: (nodeId: string) => void
  onToggleCollapse?: (nodeId: string) => void
}

/**
 * Per-type badge icon. Type is encoded by icon + label only — never by a
 * background colour — so it stays legible for colour-blind users and in dark
 * mode. Colour on a node is reserved for state (selected / hover).
 */
const TYPE_ICON: Record<OrgNodeType, LucideIcon> = {
  company: Building2,
  division: Network,
  department: Boxes,
  team: Users
}

export const OrgChartNodeCard = ({
  node,
  isSelected,
  isCollapsed,
  hasChildren,
  onClick,
  onToggleCollapse
}: Props) => {
  const { t } = useTranslation()

  const isCompany = node.type === 'company'
  const BadgeIcon = TYPE_ICON[node.type]
  const showCollapseButton =
    hasChildren && node.type !== 'team' && onToggleCollapse

  const handleClick = () => {
    if (onClick) {
      onClick(node.id)
    }
  }

  const handleCollapseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onToggleCollapse) {
      onToggleCollapse(node.id)
    }
  }

  return (
    <div
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick()
            }
          : undefined
      }
      style={{
        background: 'var(--ant-color-bg-container)',
        border: isSelected
          ? '1.5px solid var(--ant-color-primary)'
          : isCompany
            ? '1.5px solid var(--ant-color-border)'
            : '1px solid var(--ant-color-border-secondary)',
        borderRadius: 10,
        padding: '7px 12px',
        textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 5,
        boxShadow: isSelected
          ? '0 0 0 3px var(--ant-color-primary-bg), 0 1px 4px rgba(0, 0, 0, 0.08)'
          : '0 1px 4px rgba(0, 0, 0, 0.08)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        position: 'relative'
      }}
    >
      {/* Collapse toggle button — visually distinct from node body */}
      {showCollapseButton && (
        <div
          onClick={handleCollapseClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              onToggleCollapse(node.id)
            }
          }}
          title={t('workspace.orgChart.collapseHint')}
          style={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'var(--ant-color-bg-container)',
            border: '1.5px solid var(--ant-color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 2,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)'
          }}
        >
          {isCollapsed ? (
            <ChevronRight size={12} color="var(--ant-color-text-secondary)" />
          ) : (
            <ChevronDown size={12} color="var(--ant-color-text-secondary)" />
          )}
        </div>
      )}

      {/* Type badge — icon + label. The company badge is solid to mark the apex. */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          maxWidth: '100%',
          padding: '1px 8px',
          borderRadius: 999,
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.8px',
          lineHeight: 1.6,
          textTransform: 'uppercase',
          background: isCompany
            ? 'var(--ant-color-primary)'
            : 'var(--ant-color-fill-quaternary)',
          color: isCompany
            ? 'var(--ant-color-text-light-solid)'
            : 'var(--ant-color-text-secondary)',
          border: isCompany ? 'none' : '1px solid var(--ant-color-border)'
        }}
      >
        <BadgeIcon size={11} strokeWidth={2} style={{ flexShrink: 0 }} />
        {t(`workspace.orgChart.types.${node.type}`)}
      </span>

      {/* Identity block — name / lead / metrics kept tight; the outer gap is
          what separates this group from the badge above. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          minWidth: 0
        }}
      >
        <Text
          strong
          ellipsis={{ tooltip: node.name }}
          style={{
            display: 'block',
            width: '100%',
            fontSize: 13,
            color: 'var(--ant-color-text)',
            lineHeight: 1.25
          }}
        >
          {node.name}
        </Text>

        {/* Unit lead — existing data, surfaced on the card (email stays in detail). */}
        {node.owner && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              maxWidth: '100%',
              fontSize: 11,
              lineHeight: 1.3,
              color: 'var(--ant-color-text-secondary)'
            }}
          >
            <User size={11} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {node.owner.name}
            </span>
          </span>
        )}

        <div
          style={{
            fontSize: 10,
            lineHeight: 1.3,
            color: 'var(--ant-color-text-tertiary)',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {node.childCount > 0 && (
            <span>{node.childCount} sub&nbsp;&middot;&nbsp;</span>
          )}
          <span>
            {node.memberCount} {t('workspace.orgChart.membersLabel')}
          </span>
        </div>
      </div>
    </div>
  )
}
